import React, { useState } from 'react';
import { Controller } from 'react-hook-form';

const KeywordsInput = ({ control, name, required, onChange, label = 'Keywords' }) => {
  const [inputValue, setInputValue] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  const processCommaSeparatedInput = (value, fieldOnChange) => {
    if (value.includes(',')) {
      // Split by comma and process each part
      const parts = value.split(',').map(part => part.trim()).filter(part => part.length > 0);
      
      if (parts.length > 0) {
        // Add all non-empty parts as keywords
        const newKeywords = [...keywords];
        parts.forEach(part => {
          if (part && !newKeywords.includes(part)) {
            newKeywords.push(part);
          }
        });
        
        setKeywords(newKeywords);
        setInputValue(''); // Clear input after processing
        fieldOnChange(newKeywords);
        return true; // Indicates that input was processed
      }
    }
    return false; // Input was not processed
  };

  const handleInputChange = (e, fieldOnChange) => {
    const value = e.target.value;
    setInputValue(value);
    
    // Process comma-separated input
    processCommaSeparatedInput(value, fieldOnChange);
  };

  const handleKeyPress = (e, fieldOnChange) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addKeyword(fieldOnChange);
    }
  };

  const addKeyword = (fieldOnChange) => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue && !keywords.includes(trimmedValue)) {
      const newKeywords = [...keywords, trimmedValue];
      setKeywords(newKeywords);
      fieldOnChange(newKeywords);
      setInputValue('');
    }
  };

  const removeKeyword = (indexToRemove, fieldOnChange) => {
    const newKeywords = keywords.filter((_, index) => index !== indexToRemove);
    setKeywords(newKeywords);
    fieldOnChange(newKeywords);
  };

  const handleInputBlur = (fieldOnChange) => {
    if (inputValue.trim()) {
      addKeyword(fieldOnChange);
    }
  };

  const handleEditKeyword = (index, keyword) => {
    setEditingIndex(index);
    setEditingValue(keyword);
  };

  const handleSaveEdit = (fieldOnChange) => {
    if (!editingValue.trim()) return;
    
    const trimmedValue = editingValue.trim();
    const newKeywords = [...keywords];
    newKeywords[editingIndex] = trimmedValue;
    
    setKeywords(newKeywords);
    fieldOnChange(newKeywords);
    setEditingIndex(null);
    setEditingValue('');
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingValue('');
  };

  return (
    <Controller
      control={control}
      name={name}
      rules={{ required: required ? `${label} is required` : false }}
      render={({ field }) => {
        // Sync with form field value when it changes
        if (field.value && Array.isArray(field.value) && JSON.stringify(field.value) !== JSON.stringify(keywords)) {
          setKeywords(field.value);
        }

        return (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {label}
              {required && <span className="text-red-500 ml-1">*</span>}
            </label>
            
            {/* Input field */}
            <input
              type="text"
              value={inputValue}
              onChange={(e) => handleInputChange(e, field.onChange)}
              onKeyPress={(e) => handleKeyPress(e, field.onChange)}
              onBlur={() => handleInputBlur(field.onChange)}
              placeholder="Type keywords separated by commas or press Enter"
              title="Type keywords separated by commas (e.g., 'test, right, nice') or press Enter to add"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            
            {/* Keywords pills */}
            <div className="flex flex-wrap gap-2 min-h-[40px] p-2 border border-gray-200 rounded-md bg-gray-50">
              {keywords.map((keyword, index) => (
                <div key={index}>
                  {editingIndex === index ? (
                    // Edit mode
                    <div className="bg-white border border-blue-300 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                      <input
                        type="text"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        className="outline-none text-sm min-w-[100px]"
                        autoFocus
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveEdit(field.onChange);
                          } else if (e.key === 'Escape') {
                            handleCancelEdit();
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => handleSaveEdit(field.onChange)}
                        className="text-green-600 hover:text-green-800 font-bold"
                        title="Save"
                      >
                        ✓
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="text-red-600 hover:text-red-800 font-bold"
                        title="Cancel"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    // Display mode
                    <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                      <span 
                        className="cursor-pointer hover:text-blue-900"
                        onClick={() => handleEditKeyword(index, keyword)}
                        title="Click to edit this keyword"
                      >
                        {keyword}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeKeyword(index, field.onChange)}
                        className="text-blue-600 hover:text-blue-800 font-bold"
                        title="Remove this keyword"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {keywords.length === 0 && (
                <span className="text-gray-400 text-sm">No keywords added yet</span>
              )}
            </div>
          </div>
        );
      }}
    />
  );
};

export default KeywordsInput;